<template>
  <sForm
    ref="criteriaTable"
    :model="{row: rows}"
    :rules="rowsRules">
    <v-data-table
      calculate-widths
      :class="'criteria-table criteria ' + (editable ? 'editable ' : '') + tdColor"
      data-qa="criteria-table"
      disable-pagination
      :headers="headers"
      hide-default-footer
      hide-default-header
      :items="rows"
      :no-data-text="$t('Tenders.NoData')">
      <template #[`header`]="{ props }">
        <thead class="v-data-table-header">
          <tr data-qa="headers">
            <th
              v-for="(item, index) in props.headers"
              :key="index"
              class="text-start h9-text"
              :data-qa="item.value"
              :style="`width: ${item.width}`">
              <v-row
                class="flex flex-between gap-2 flex-nowrap"
                no-gutters>
                <template v-if="item.value == 'data'">
                  <v-col :data-qa="`criteria-table-${item.value}-header`">
                    <span
                      v-if="editable"
                      class="text-start h9-text h5-headline secondary__red--text">*</span>
                    <span>{{ $t('Tenders.Data') }}</span>
                  </v-col>

                  <v-col :data-qa="`criteria-table-${item.value}-header-quantity`">
                    <span>{{ $t('Tenders.Quantity') }}</span>
                  </v-col>
                </template>
                <template v-else>
                  <v-col :data-qa="`criteria-table-${item.value}-header`">
                    <span
                      v-if="item.required && editable"
                      class="h9-text h5-headline secondary__red--text">*</span>
                    {{ item.text }}
                  </v-col>
                </template>
                <InfoIconWithTooltip
                  v-if="!!item.info"
                  color="#656262"
                  :content="item.info"/>
              </v-row>
            </th>
          </tr>
        </thead>
      </template>
      <template #[`item`]="{ item, index, headers: heads }">
        <tr
          :key="index"
          :data-qa="item.id || item.key">
          <td
            v-for="header in heads"
            :key="header.value + index"
            class="text-start"
            :class="[ (header.required && item[header.required] == null && error_fields && 'secondary__red-border ') || tdColor, header.value + '-td' ]"
            :data-qa="header.value">
            <template v-if="header.value == 'title'">
              <sFormItem
                :data-qa="`table-row-${index}-title`"
                mode="table-cell"
                :prop="`row[${index}].title`"/>
              <HoverAddData
                :color="tdColor"
                :disable="!editable"
                :error="!item.title && error_fields && $t('Tenders.YouMustFillVField')"
                full_height
                text_class="pa-3 align-start "
                :value="item.title">
                <template #activator="{ activate }">
                  <div
                    class="d-flex pa-3 flex-grow-1"
                    :class=" ( tdColor )">
                    <div
                      class="d-flex flex-grow-1 pr-4"
                      :class="{'secondary__red--text' : item.titleError}"
                      @click="activate">
                      {{ item.title }}
                    </div>
                    <InfoIconWithTooltip
                      v-if="!!item.titleError"
                      :content="item.titleError"
                      error/>
                  </div>
                </template>
                <template #editor="{ deactivate }">
                  <CustomTextarea
                    area_class_name="textarea pa-3 mt-0 h7-text "
                    autofocus
                    class-name="align-self-stretch"
                    max_length="254"
                    rows="1"
                    :value.sync="item.title"
                    @blur="validCriteriaTitle(item, deactivate)"
                    @keydown_enter="validCriteriaTitle(item, deactivate)"
                    @keydown_esc="validCriteriaTitle(item, deactivate)"/>
                </template>
              </HoverAddData>
            </template>

            <template v-if="header.value == 'data'">
              <sFormItem
                :data-qa="`table-row-${index}-data`"
                mode="table-cell"
                :prop="`row[${index}].data`"/>
              <CriterionType
                :key="`${item.id || item.key}_${item.calculationFormulaError || ''}${item.calculationFormula || ''}${item.criterionControlType || ''}`"
                :can_edit="editable"
                :card.sync="card"
                :criteria="rows"
                :dense="!editable"
                :error_fields="error_fields"
                :item="item"
                :reduced-price-available="reducedPriceAvailable"
                :save-card="saveCard"
                :td-color="tdColor"/>
            </template>
            <template v-if="header.value == 'criterionBindingLevel'">
              <sFormItem
                :data-qa="`table-row-${index}-criterionBindingLevel`"
                mode="table-cell"
                :prop="`row[${index}].criterionBindingLevel`"/>
              <HoverAddData
                :color="tdColor "
                :disable="!editable"
                :error="typeof item.criterionBindingLevel != 'boolean' && error_fields && $t('Tenders.YouMustFillVField')"
                :formatter="text => formatText(text, true, 'To') "
                full_height
                :label="$t('Common.Add')"
                menu
                text_class="pa-3 align-start "
                :value="item.criterionBindingLevel">
                <template #menu="{ deactivate }">
                  <CriteriaRadioCard
                    :key="item.criterionBindingLevel"
                    :card="card"
                    :close="deactivate"
                    :header="$t('Tenders.ChooseWhichCriterionToBindTo') + '?'"
                    :items="items"
                    :model.sync="item.criterionBindingLevel"
                    :options="binding_levels"
                    :save-card="saveCard"
                    :submit="(elem, val) => setCriterionBindingLevel(elem || item, val, deactivate)"
                    type="criterionBindingLevel"/>
                </template>
              </HoverAddData>
            </template>

            <template v-if="header.value == 'description'">
              <HoverAddData
                :color="tdColor "
                :disable="!editable"
                full_height
                :label="$t('Common.AddDescription')"
                text_class="pa-3 align-start "
                :value="item.description">
                <template #editor="{ deactivate }">
                  <CustomTextarea
                    area_class_name="textarea pa-3  h7-text "
                    autofocus
                    class-name="align-self-stretch"
                    max_length="4000"
                    rows="1"
                    :value.sync="item.description"
                    @blur="deactivate"
                    @keydown_esc="deactivate"/>
                </template>
              </HoverAddData>
            </template>

            <template v-if="header.value == 'document'">
              <HoverAddData
                :color="tdColor "
                :disable="!editable || item.criterionKind !== 'DEFAULT'"
                :error="!item.attachmentMode && error_fields && $t('Tenders.YouMustFillVField')"
                :formatter="text => formatText(text) "
                full_height
                menu
                text_class="pa-3 align-start "
                :value="item.attachmentMode">
                <template #menu="{ deactivate }">
                  <CriteriaRadioCard
                    :key="item.attachmentMode"
                    :close="deactivate"
                    :header="$t('Tenders.AddDocumentsToCriterion')"
                    :items="items"
                    :model.sync="item.attachmentMode"
                    :options="attachment_modes"
                    :save-card="saveCard"
                    :submit="(elem, val) => setAttachmentMode(elem || item, val , deactivate)"
                    type="attachmentMode"/>
                </template>
              </HoverAddData>
            </template>

            <template v-if="header.value == 'analogues'">
              <HoverAddData
                :color="tdColor "
                :disable="!editable || ckeckAnalogsLocked(item)"
                :error="criterionKindError(item)"
                :formatter="text => formatCriterionKind(text) "
                full_height
                :label="$t('Common.Add')"
                left
                menu
                text_class="pa-3 align-start "
                :value="item.criterionKind">
                <template #menu="{ deactivate }">
                  <CriteriaRadioCard
                    :key="item.criterionKind"
                    :close="deactivate"
                    :header="$can('show', 'useCriterionKindSelection') ? '' : $t('Tenders.AllowParticipantAddAnalogueToCriterion')"
                    :item="item"
                    :items="items"
                    :model.sync="item.criterionKind"
                    :options="criterionKindOptions"
                    :save-card="saveCard"
                    :submit="(elem, val) => setAnalog(elem || item, val , deactivate)"
                    type="criterionKind"/>
                </template>
              </HoverAddData>
            </template>
            <template v-if="header.value == 'display'">
              <sFormItem
                :data-qa="`table-row-${index}-visible`"
                mode="table-cell"
                :prop="`row[${index}].dynamicCriterions`"/>
              <HoverAddData
                :key="rows.length"
                ref="hoverDisplayCondidions"
                bottom
                :color="tdColor"
                :disable="!editable || diisplayIsDisabled(item)"
                :error="!item.isDisplayed && error_fields && $t('Tenders.YouMustFillVField')"
                full_height
                left
                menu
                menu_width="650"
                text_class="pa-3 align-start"
                :value="item.isDisplayed">
                <template #menu="{ deactivate, activ }">
                  <CriterionDisplayCondidions
                    v-if="activ"
                    :close="deactivate"
                    :criteria="items"
                    :item="item"
                    :save-card="saveCard"
                    @needResize="needResize(index)"/>
                </template>
                <template #activator>
                  <sPopover
                    :disabled="!item.dynamicCriterions.length || invalidDynamicCriterions[index].isInvalid"
                    trigger="hover"
                    width="400">
                    <div
                      slot="reference"
                      class="flex flex-between">
                      <sBadge
                        :content="item.dynamicCriterions.length"
                        inline
                        :value="item.dynamicCriterions.length">
                        {{ formatText(item.isDisplayed) }}
                      </sBadge>
                      <InfoIconWithTooltip
                        v-if="invalidDynamicCriterions[index].isInvalid"
                        :content="$t('Validate.ImpossibleConditionConfigureTheCriterionDisplayCondition')"
                        error/>
                    </div>
                    <div class="py-1 px-4">
                      <DynamicCondition
                        v-for="(criterionHint, indHint) in item.dynamicCriterions"
                        :key="`hint-${index}-${indHint}`"
                        :dynamic-criterion="criterionHint"
                        :index="indHint"
                        :item="item"
                        :rows="rows"
                        :type="'hint'"/>
                    </div>
                  </sPopover>
                  <DynamicCondition
                    v-for="(criterion, ind) in [...item.dynamicCriterions].splice(0, 2)"
                    :key="ind"
                    :dynamic-criterion="criterion"
                    :index="ind"
                    :item="item"
                    :rows="rows"
                    :type="'row'"/>
                  <div v-if="item.dynamicCriterions.length > 3">
                    ...
                  </div>
                </template>
              </HoverAddData>
            </template>
            <template v-if="header.value == 'menu'">
              <RowMenu
                class-name="mt-2 ml-1"
                @cancelSelection="deleteItem(item)"
                @copyItem="() => copyItem(item)"/>
            </template>
          </td>
        </tr>
      </template>
    </v-data-table>
  </sForm>
</template>
<script>
import RowMenu from 'components/common/blocks/RowMenu.vue'
import HoverAddData from 'components/common/blocks/HoverAddData.vue'
import CustomTextarea from 'components/common/inputs/CustomTextarea.vue'
import CriterionType from 'components/tenders/critetia/CriterionType.vue'
import DynamicCondition from 'components/tenders/critetia/DynamicCondition.vue'
import CriteriaRadioCard from 'components/tenders/critetia/CriteriaRadioCard.vue'
import InfoIconWithTooltip from 'components/common/elements/InfoIconWithTooltip.vue'
import CriterionDisplayCondidions from 'components/tenders/critetia/CriterionDisplayCondidions.vue'

import { criteriasMixin, modalMixin, functionRandom } from 'mixins'

export default {
  name: 'criteriaTable',
  mixins: [ modalMixin, criteriasMixin, functionRandom ],
  data() {
    return {
      // rows: []
      criterionLevel_all: false,
      attachmentMode_all: false,
      rowsRules: {}
    }
  },
  props: {
    items: Array,
    editable: Boolean,
    tdColor: {
      type: String,
      default: () => ''
    },
    card: Object,
    has_empty_fields: Boolean,
    error_fields: Boolean,
    bidding_type: Object,
    tender: Object
  },
  computed: {
    criterionKindOptions() {
      const options = [
        { label: this.$t('Tenders.Default'), value: 'DEFAULT' },
        { label: this.$t('Tenders.Analogue'), value: 'ANALOG' },
      ]
      if (this.$can('show', 'useCriterionKindSelection')) {
        options.push({ label: this.$t('Tenders.Variant'), value: 'VARIANT' })
      }
      return options
    },
    invalidDynamicCriterions() {
      return this.items?.map(item => ({
        ...item,
        isInvalid: this.checkInValidAllDynamic(this.rows, item)
      })) || []
    },
    rows: {
      get() {
        return this.items//?.slice(0)
      },
      set(val) {
        //this.createCriteriaRules()
        this.$emit('update:items', val)
      }
    },
    reducedPriceAvailable() {
      return this.bidding_type?.reducedPriceAvailable
    },
    headers() {
      let  td_color = this.error_fields ? ['', '', 'secondary__red-border'] : this.tdColor ,
        headers = [
          {text: this.$t('Tenders.Criterion'), value: 'title', width: '24%',sortable: false, cellClass: td_color, required: 'title'  },
          // {text: this.$t('Tenders.Variable'), value: 'variable', width: '12%', sortable: false, cellClass: td_color, info: this.$t('Tenders.SpecifyVariableInfo') },
          {text: this.$t('Tenders.Data'), value: 'data', width: '14%', sortable: false, cellClass: td_color, required: 'criterionControlType', info: this.$t('Tenders.WeightValueInfo') },
          {text: this.$t('Tenders.Applies'), value: 'criterionBindingLevel', width: '10%', sortable: false , cellClass: td_color , required: 'criterionBindingLevel', info: this.$t('Tenders.CriterionLevelInfo')},
          {text: this.$t('Tenders.AdditionalDescription'), value: 'description', width: '14%', sortable: false , cellClass: td_color },
          {text: this.$t('Tenders.AttachDocument'), value: 'document', width: '14%', sortable: false , cellClass: td_color , required: 'attachmentMode'},
          {text: this.$t('Tenders.Display'), value: 'display', width: '12%', sortable: false , cellClass: td_color , required: 'isDisplayed'},
        ]
      if ( this.bidding_type?.analogAvailable ) {
        headers.splice(-1, 0,
          {text: this.$t('Tenders.CriterionType'), value: 'analogues', width: '10%', sortable: false, cellClass: td_color, required: 'criterionKind', info: this.$t('Tenders.UseInAnalogNamingInfo')})
        headers[0].width = '14%'
      }
      if  ( this.editable ) {
        headers.push({text: '', value: 'menu', sortable: false, width: '2%', cellClass: td_color })
      }
      return headers
    },
    attachment_modes() {
      return  [
        { label: this.$t('Tenders.REQUIRED'), value: 'REQUIRED'},
        { label: this.$t('Tenders.AVAILABLE'), value: 'AVAILABLE'},
        { label: this.$t('Tenders.NOT_AVAILABLE'), value: 'NOT_AVAILABLE'},
      ]
    },
    binding_levels() {
      return [
        { label: this.$t('Tenders.ToTender'), value: 'TENDER'},
        { label: this.$t('Tenders.ToLot'), value: 'LOT'},
      ]
    },
  },
  methods: {
    needResize(index) {
      const hoverDisplayCondidions = this.$refs.hoverDisplayCondidions[index]
      if (hoverDisplayCondidions && hoverDisplayCondidions.adjustMenuPosition) {
        hoverDisplayCondidions.adjustMenuPosition()
      }
    },
    validateTable() {
      return this.$refs.criteriaTable.validate()
    },
    /**
     * проверяем что название критерия уникальное
     */
    isTitleUnique(index) {
      const titleToCheck = this.rows[index].title

      for (let i = 0; i < this.rows.length; i++) {
        if (i !== index && this.rows[i].title === titleToCheck) {
          return false // Если находим дубликат, возвращаем false
        }
      }

      return true // Если дубликаты не найдены, возвращаем true
    },
    createCriteriaRules() {
      this.rowsRules = {}
      if (!this.items) {
        return
      }
      const that = this
      this.items.forEach((row, index) => {
        const keyPrefix = `row[${index}]`
        this.rowsRules[`${keyPrefix}.title`] = {
          type: 'string',
          trigger: 'change',
          transform(value) {
            return value?.trim() ?? ''
          },
          /*required: true,
          min: 2,
          message: this.$t('Validate.minLength', {number: 2, type: this.$tc('Common.characters', 2) }),*/
          validator(rule, value, callback) {
            if (!value) {
              callback(new Error(that.$t('Validate.Required')))
            }
            if (value.length < 2) {
              callback(new Error(that.$t('Validate.minLength', {number: 2, type: this.$tc('Common.characters', 2) })))
            }
            if (value.length > 254) {
              callback(new Error(that.$t('Validate.maxLength', {number: 254, type: this.$tc('Common.characters', 4) })))
            }
            if (!that.isTitleUnique(index)) {
              callback(new Error(that.$t('Tenders.TheNameOfTheCriterionMustBeUnique')))
            }
            callback()
          },
        }

        this.rowsRules[`${keyPrefix}.data`] = {
          type: 'string',
          trigger: 'change',
          transform(value) {
            return value?.trim() ?? ''
          },
          required: true,
          message: that.$t('Validate.Required')
        }

        this.rowsRules[`${keyPrefix}.criterionBindingLevel`] = {
          type: 'string',
          trigger: 'change',
          required: true,
          message: that.$t('Validate.Required')
        }


        //dictionaryValues

      })
    },
    formatText(text, big, prefix) {
      if ( typeof text == 'boolean' ) {
        return text ? this.$t('Tenders.Yes') : this.$t('Tenders.No')
      } else {
        return text ? this.$t('Tenders.' + ( prefix || '') + (big ? this.bigFirstLetter(text) : text )) : ''
      }

    },
    formatCriterionKind(text) {
      const map = {
        DEFAULT: this.$t('Tenders.Default'),
        ANALOG: this.$t('Tenders.Analogue'),
        VARIANT: this.$t('Tenders.Variant'),
      }
      return map[text] || ''
    },
    criterionKindError(item) {
      return !item.criterionKind && this.error_fields && this.$t('Tenders.YouMustFillVField')
    },
    async copyItem(item) {
      let guid = await this.$store.dispatch('getNewId'),
        new_item = this.$_.cloneDeep( {
          ...item,
          formulaCode: '',
          key: (Math.max(...this.rows.map(el => el.id || el.key ) ) ) + 1,
          id: null,
          criteriaMark: guid,
          criterionCode: '',
          dictionaryValues: item.dictionaryValues.map(el => ({ ...el, id: null, key: el.id || el.key }) ),
          useInRedFormula: false
        })

      this.rows.push(new_item)
      this.nameValidate(new_item)
      this.saveCard()
    },
    deleteItem(item) {
      let ind = this.items.findIndex(el => item.id ? el.id == item.id : item.key == el.key )
      this.rows.splice(ind, 1)
      this.validateAllCriteria()
      this.saveCard()
    },
    diisplayIsDisabled(item) {
      return this.checkDisplayIsLocked(item, this.rows) && item.isDisplayed == 'ALWAYS'
    },
    getDynamicTitle(item) {
      return item.targetCriterionTitle || this.rows.find(crit => crit.criteriaMark == item.targetCriterionMark)?.title || item.targetCriterionMark
    },
    isDynamicValid(item) {
      return this.rows.some(crit => crit.criteriaMark == item.targetCriterionMark)
    },
    setCriterionBindingLevel(item, val, deactivate) {
      item.criterionBindingLevel = val
      deactivate()

      if ( item?.criterionControlType == 'CALCULATED' ) {
        item.calculationFormulaError = this.calculationFormulaValidate(item)
      }
      if ( item.criterionBindingLevel == 'TENDER' ) {
        item.criterionKind = 'DEFAULT'
      }
    },
    setAttachmentMode(item, val, deactivate) {
      if (item.criterionKind === 'DEFAULT' ) {
        item.attachmentMode = val
      }
      deactivate()
    },
    setAnalog(item, val, deactivate) {
      if ( !this.ckeckAnalogsLocked(item) ) {
        item.criterionKind = val
      }

      deactivate()
      if (item.criterionKind !== 'DEFAULT' ) {
        item.attachmentMode = 'NOT_AVAILABLE'
        item.isDisplayed = 'ALWAYS',
        item.dynamicCriterions = []
      }
      this.validateAllCriteria()
    },
    validCriteriaTitle(item, deactivate) {
      deactivate()
      this.rows.forEach(row => {
        this.nameValidate(row)
      })
      this.saveCard()
    },
    saveCard() {
      this.saveCardToLocalStorage(this.tender, this.card)
    }
  },
  components: {
    RowMenu,
    HoverAddData,
    CriterionType,
    CustomTextarea,
    DynamicCondition,
    CriteriaRadioCard,
    InfoIconWithTooltip,
    CriterionDisplayCondidions
  },
  mounted() {
    //this.createCriteriaRules()
  }
}
</script>
<style lang="scss">
.criteria-table {
  &.v-data-table {
    .v-data-table__empty-wrapper td {
      vertical-align: inherit !important;
    }
    .v-data-table__wrapper tr{
      td, th {
        border-left: thin solid var(--v-main__green-base) !important;
        padding: 12px !important;
      }
      td {
        padding: 0  !important;
        vertical-align: top;
        background-color: var(--v-secondary__green_chosen-base) !important;
        // height: auto;
        &.secondary__red-border {
          border: 1px solid var(--v-secondary__red-base) !important;
        }
        &.main__light_green {
          background-color: var(--v-main__light_green-base) !important;
          border-bottom: thin solid var(--v-main__green-base) !important;
        }
        //.activator {
        //  min-height: 20px;
        //}
        &:nth-child(3) {
          padding-left:  0  !important;
          padding-right:  0  !important;
        }
        // &:nth-child(3), &:nth-child(4), &:nth-child(6),&:nth-child(7), &:nth-child(8) {
        //   // padding: 0  !important;
        // }
        .secondary__red--text.textarea {
          textarea {
            color: var(--v-secondary__red-base) !important;
          }
        }
      }
    }
    &.editable {
      .v-data-table__wrapper tr{
        td {
          background-color: var(--v-main__light_green-base) !important;
        }
      }
    }
  }
}

</style>
